import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { RefreshCw, Link2, PlugZap, AlertTriangle, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function InstellingenChannelDock() {
  const [status, setStatus] = useState(null);
  const [rijen, setRijen] = useState([]);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState("");
  const [alleenOngematcht, setAlleenOngematcht] = useState(false);
  const [zoek, setZoek] = useState("");

  const load = async () => {
    const [{ data: st }, { data: rows }, { data: lg }] = await Promise.all([
      api.get("/channeldock/status"),
      api.get("/channeldock/voorraad", { params: { alleen_ongematcht: alleenOngematcht } }),
      api.get("/channeldock/log", { params: { limit: 25 } }),
    ]);
    setStatus(st); setRijen(rows); setLogs(lg);
  };
  useEffect(() => { load().catch(() => toast.error("Laden mislukt")); /* eslint-disable-next-line */ }, [alleenOngematcht]);

  const doe = async (naam, fn, melding) => {
    setBusy(naam);
    try { const r = await fn(); toast.success(melding(r?.data)); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Mislukt"); }
    finally { setBusy(""); }
  };

  const koppel = async (cd_product_id, crm_sku) => {
    try {
      await api.put("/channeldock/mapping", { cd_product_id, crm_sku });
      toast.success(crm_sku ? `Gekoppeld aan ${crm_sku}` : "Koppeling verwijderd");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Koppelen mislukt"); }
  };

  const zichtbaar = rijen.filter(r => {
    const q = zoek.trim().toLowerCase();
    if (!q) return true;
    return `${r.titel} ${r.cd_sku} ${r.ean} ${r.product_reference} ${r.crm_sku}`.toLowerCase().includes(q);
  });
  const s = status?.laatste_sync || {};

  return (
    <div className="space-y-6" data-testid="instellingen-channeldock">
      <div>
        <h1 className="font-display text-3xl">ChannelDock</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Fulfilment-voorraad uitlezen en orders doorzetten. De voorraad bij ChannelDock is stand-alone: het CRM leest
          alleen uit en verandert daar niets.
        </p>
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant={status?.geconfigureerd ? "default" : "destructive"} data-testid="cd-status-badge">
            {status?.geconfigureerd ? "Verbonden met API-key" : "Geen API-key ingesteld"}
          </Badge>
          <Button variant="outline" size="sm" disabled={busy === "test"}
            onClick={() => doe("test", () => api.post("/channeldock/test"), (d) => `Verbinding OK · ${(d?.warehouses || []).length} warehouse(s)`)}
            data-testid="cd-test-btn"><PlugZap className="h-4 w-4 mr-1" />Verbinding testen</Button>
          <Button size="sm" disabled={busy === "sync"}
            onClick={() => doe("sync", () => api.post("/channeldock/sync-voorraad"), (d) => `${d?.artikelen} artikelen · ${d?.gematcht} gekoppeld · ${d?.stuks} stuks`)}
            data-testid="cd-sync-btn"><RefreshCw className={`h-4 w-4 mr-1 ${busy === "sync" ? "animate-spin" : ""}`} />Voorraad ophalen</Button>
          <Button variant="outline" size="sm" disabled={busy === "tt"}
            onClick={() => doe("tt", () => api.post("/channeldock/sync-zendingen"), (d) => `${d?.bijgewerkt} van ${d?.gecontroleerd} orders bijgewerkt`)}
            data-testid="cd-tt-btn"><Link2 className="h-4 w-4 mr-1" />Track & trace ophalen</Button>
        </div>
        {s.bijgewerkt_op && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-5 text-sm" data-testid="cd-sync-stats">
            {[["Artikelen", s.artikelen], ["Gekoppeld", s.gematcht], ["Niet gekoppeld", s.ongematcht],
              ["Stuks voorraad", s.stuks], ["Negatief", s.negatief]].map(([k, v]) => (
              <div key={k}><div className="text-xs text-muted-foreground">{k}</div><div className="font-medium text-lg">{v ?? 0}</div></div>
            ))}
            <div className="col-span-2 sm:col-span-5 text-xs text-muted-foreground">
              Laatst bijgewerkt: {new Date(s.bijgewerkt_op).toLocaleString("nl-NL")}
            </div>
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-display text-xl">Artikelen koppelen ({zichtbaar.length})</h2>
          <div className="flex items-center gap-2">
            <Input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek op naam, EAN of SKU…"
              className="w-56" data-testid="cd-zoek" />
            <Button variant={alleenOngematcht ? "default" : "outline"} size="sm"
              onClick={() => setAlleenOngematcht(v => !v)} data-testid="cd-filter-ongematcht">
              Alleen niet gekoppeld
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-xs uppercase tracking-wide">
              <tr>
                <th className="p-2 text-left">ChannelDock-artikel</th>
                <th className="p-2 text-left">EAN</th>
                <th className="p-2 text-left">Referentie</th>
                <th className="p-2 text-right">Voorraad</th>
                <th className="p-2 text-right">Beschikbaar</th>
                <th className="p-2 text-left">CRM-SKU</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {zichtbaar.map((r) => (
                <tr key={r.cd_product_id} data-testid={`cd-rij-${r.cd_product_id}`}>
                  <td className="p-2">{r.titel || "—"}</td>
                  <td className="p-2 font-mono text-xs">{r.ean || "—"}</td>
                  <td className="p-2 font-mono text-xs">{r.product_reference || r.cd_sku || "—"}</td>
                  <td className={`p-2 text-right font-medium ${r.stock < 0 ? "text-destructive" : ""}`}>
                    {r.stock < 0 && <AlertTriangle className="h-3 w-3 inline mr-1" />}{r.stock}
                  </td>
                  <td className="p-2 text-right text-muted-foreground">{r.available_stock}</td>
                  <td className="p-2">
                    <Input defaultValue={r.crm_sku || ""} placeholder="SKU…" className="h-8 w-32"
                      onBlur={(e) => { const v = e.target.value.trim(); if (v !== (r.crm_sku || "")) koppel(r.cd_product_id, v); }}
                      data-testid={`cd-sku-${r.cd_product_id}`} />
                  </td>
                </tr>
              ))}
              {zichtbaar.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">Nog geen artikelen — haal eerst de voorraad op.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <InboundSectie />

      <div className="bg-card border border-border rounded-xl p-6">
        <h2 className="font-display text-xl mb-3">API-logboek</h2>        <div className="divide-y divide-border text-sm" data-testid="cd-log">
          {logs.map((l) => (
            <div key={l.id} className="py-2 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium">{l.actie} <span className="text-xs text-muted-foreground">{l.methode} {l.pad}</span></div>
                {l.detail && <div className="text-xs text-destructive truncate">{l.detail}</div>}
              </div>
              <div className="text-right shrink-0">
                <Badge variant={l.ok ? "outline" : "destructive"}>{l.status || "—"}</Badge>
                <div className="text-[11px] text-muted-foreground mt-1">{new Date(l.tijd).toLocaleString("nl-NL")}</div>
              </div>
            </div>
          ))}
          {logs.length === 0 && <p className="text-muted-foreground italic py-2">Nog geen API-calls.</p>}
        </div>
      </div>
    </div>
  );
}

const STATUS_LABEL = { aangemeld: "Aangemeld", onderweg: "Onderweg", ontvangen: "Ontvangen & ingeboekt" };

export const InboundSectie = ({ voorafRegels = [], voorafRef = "", inkooporderId = null, compact = false, onKlaar }) => {
  const [items, setItems] = useState([]);
  const [levs, setLevs] = useState([]);
  const [producten, setProducten] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    ref: voorafRef, supplier_id: "", delivery_date: "", pallets: 0, boxes: 0,
    opmerking: "", afboeken_van: inkooporderId ? "geen" : "warehouse",
    regels: voorafRegels.length ? voorafRegels : [{ sku: "", aantal: "" }],
  });

  const load = async () => {
    const [{ data: inb }, { data: l }] = await Promise.all([
      api.get("/channeldock/inbound"), api.get("/channeldock/leveranciers").catch(() => ({ data: [] })),
    ]);
    setItems(inb); setLevs(l);
    if (!producten.length) {
      const { data: p } = await api.get("/producten");
      setProducten(Array.isArray(p) ? p : p.items || []);
    }
  };
  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, []);

  const zetRegel = (i, veld, waarde) => setF(p => ({
    ...p, regels: p.regels.map((r, idx) => idx === i ? { ...r, [veld]: waarde } : r),
  }));

  const aanmelden = async () => {
    if (!f.ref.trim() || !f.supplier_id) return toast.error("Referentie en leverancier zijn verplicht");
    const regels = f.regels.filter(r => r.sku && Number(r.aantal) > 0).map(r => ({ sku: r.sku, aantal: Number(r.aantal) }));
    if (!regels.length) return toast.error("Voeg minimaal één regel met aantal toe");
    setBusy(true);
    try {
      await api.post("/channeldock/inbound", { ...f, supplier_id: Number(f.supplier_id), regels, inkooporder_id: inkooporderId });
      toast.success("Levering aangemeld bij ChannelDock");
      setOpen(false); await load(); onKlaar?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Aanmelden mislukt"); }
    finally { setBusy(false); }
  };

  const sync = async () => {
    try {
      const { data } = await api.post("/channeldock/inbound/sync");
      toast.success(`${data.bijgewerkt} van ${data.gecontroleerd} leveringen bijgewerkt`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bijwerken mislukt"); }
  };

  const verwijder = async (id) => {
    if (!window.confirm("Aanmelding intrekken bij ChannelDock?")) return;
    try { await api.delete(`/channeldock/inbound/${id}`); toast.success("Ingetrokken"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Intrekken mislukt"); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="cd-inbound">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-xl">Voorraad naar ChannelDock</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {compact
              ? "Meld deze inkooporder aan als levering bij ChannelDock; de regels staan al klaar."
              : "Meld een levering aan (inkoop die direct naar ChannelDock gaat, of eigen voorraad die je opstuurt) en volg de ontvangst."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={sync} data-testid="cd-inbound-sync">
            <RefreshCw className="h-4 w-4 mr-1" />Ontvangst bijwerken
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="cd-inbound-nieuw"><Plus className="h-4 w-4 mr-1" />Levering aanmelden</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="cd-inbound-dialog">
              <DialogHeader><DialogTitle>Levering aanmelden bij ChannelDock</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Referentie</Label>
                    <Input value={f.ref} onChange={(e) => setF({ ...f, ref: e.target.value })}
                      placeholder="Bijv. INK-2026-014" data-testid="cd-inbound-ref" /></div>
                  <div><Label>Afzender (leverancier bij ChannelDock)</Label>
                    <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                      value={f.supplier_id} onChange={(e) => setF({ ...f, supplier_id: e.target.value })}
                      data-testid="cd-inbound-supplier">
                      <option value="">Kies…</option>
                      {levs.map(l => <option key={l.id} value={l.id}>{l.naam}</option>)}
                    </select></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Leverdatum</Label>
                    <Input type="date" value={f.delivery_date} onChange={(e) => setF({ ...f, delivery_date: e.target.value })}
                      data-testid="cd-inbound-datum" /></div>
                  <div><Label>Pallets</Label>
                    <Input type="number" value={f.pallets} onChange={(e) => setF({ ...f, pallets: Number(e.target.value) })} /></div>
                  <div><Label>Dozen</Label>
                    <Input type="number" value={f.boxes} onChange={(e) => setF({ ...f, boxes: Number(e.target.value) })} /></div>
                </div>
                <div className={compact ? "hidden" : ""}>
                  <Label>Afboeken van eigen voorraad</Label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                    value={f.afboeken_van} onChange={(e) => setF({ ...f, afboeken_van: e.target.value })}
                    data-testid="cd-inbound-afboeken">
                    <option value="geen">Niet afboeken (komt rechtstreeks van de leverancier)</option>
                    <option value="warehouse">Eigen warehouse</option>
                    <option value="leverancier">Voorraad bij leverancier</option>
                  </select>
                </div>
                <div>
                  <Label>Regels</Label>
                  <div className="space-y-2 mt-1 max-h-56 overflow-y-auto">
                    {f.regels.map((r, i) => (
                      <div key={i} className="flex gap-2">
                        <select className="flex-1 h-9 rounded-md border border-input bg-background px-2 text-sm"
                          value={r.sku} onChange={(e) => zetRegel(i, "sku", e.target.value)}
                          data-testid={`cd-inbound-sku-${i}`}>
                          <option value="">Kies product…</option>
                          {producten.map(p => <option key={p.id} value={p.sku}>{p.sku} — {p.naam}</option>)}
                        </select>
                        <Input type="number" className="w-24" placeholder="Aantal" value={r.aantal}
                          onChange={(e) => zetRegel(i, "aantal", e.target.value)} data-testid={`cd-inbound-aantal-${i}`} />
                        <Button variant="ghost" size="sm" className="text-destructive"
                          onClick={() => setF(p => ({ ...p, regels: p.regels.filter((_, idx) => idx !== i) }))}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" className="mt-2"
                    onClick={() => setF(p => ({ ...p, regels: [...p.regels, { sku: "", aantal: "" }] }))}
                    data-testid="cd-inbound-regel-toevoegen">
                    <Plus className="h-4 w-4 mr-1" />Regel toevoegen
                  </Button>
                </div>
                <div><Label>Opmerking voor het warehouse</Label>
                  <Input value={f.opmerking} onChange={(e) => setF({ ...f, opmerking: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
                <Button onClick={aanmelden} disabled={busy} data-testid="cd-inbound-verstuur">
                  {busy ? "Aanmelden…" : "Aanmelden"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="divide-y divide-border">
        {(compact ? items.filter(x => x.inkooporder_id === inkooporderId) : items).map(inb => {
          const besteld = (inb.regels || []).reduce((s, r) => s + Number(r.aantal || 0), 0);
          const ontvangen = (inb.regels || []).reduce((s, r) => s + Number(r.ontvangen || 0), 0);
          return (
            <div key={inb.id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`cd-inbound-rij-${inb.id}`}>
              <div className="min-w-0">
                <div className="font-medium">{inb.ref} <span className="text-xs text-muted-foreground">· levering {inb.cd_delivery_id}</span></div>
                <div className="text-xs text-muted-foreground">
                  {(inb.regels || []).length} regels · {besteld} stuks aangemeld · {ontvangen} ingeboekt
                  {inb.delivery_date ? ` · verwacht ${inb.delivery_date}` : ""}
                  {inb.afboeken_van !== "geen" ? ` · afgeboekt van ${inb.afboeken_van}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={inb.status === "ontvangen" ? "default" : "outline"}>{STATUS_LABEL[inb.status] || inb.status}</Badge>
                {inb.status !== "ontvangen" && (
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => verwijder(inb.id)}
                    data-testid={`cd-inbound-verwijder-${inb.id}`}><Trash2 className="h-4 w-4" /></Button>
                )}
              </div>
            </div>
          );
        })}
        {(compact ? items.filter(x => x.inkooporder_id === inkooporderId) : items).length === 0 &&
          <p className="text-muted-foreground italic py-2">Nog geen leveringen aangemeld.</p>}
      </div>
    </div>
  );
};
